/**
 * AIImportDialog
 *
 * Dialog component for importing AI-corrected exercise JSON.
 * Validates the JSON and optionally displays a before/after comparison.
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { Exercice } from '../../types';
import {
  validateAIImport,
  AIImportValidationResult,
  AIImportFieldError
} from '../../utils/exerciceAIExport';
import ComparisonView from './ComparisonView';

// ============================================================================
// TYPES
// ============================================================================

type ImportDialogStep = 'input' | 'comparison';

interface AIImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: Partial<Omit<Exercice, 'id'>>, options?: { autoSave?: boolean }) => void;
  currentExercice?: Exercice; // Required for comparison mode
}

// ============================================================================
// COMPONENT
// ============================================================================

const AIImportDialog: React.FC<AIImportDialogProps> = ({
  open,
  onClose,
  onImport,
  currentExercice
}) => {
  const [jsonText, setJsonText] = useState('');
  const [validationResult, setValidationResult] = useState<AIImportValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [step, setStep] = useState<ImportDialogStep>('input');
  const [validatedData, setValidatedData] = useState<Partial<Omit<Exercice, 'id'>> | null>(null);

  /**
   * Reset state when dialog closes
   */
  const handleClose = useCallback(() => {
    setJsonText('');
    setValidationResult(null);
    setIsValidating(false);
    setShowComparison(false);
    setStep('input');
    setValidatedData(null);
    onClose();
  }, [onClose]);

  /**
   * Validate JSON on change (debounced)
   */
  const handleJsonChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setJsonText(text);

    // Clear previous validation when text changes
    if (validationResult) {
      setValidationResult(null);
    }
  }, [validationResult]);

  /**
   * Handle toggle change
   */
  const handleToggleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setShowComparison(e.target.checked);
  }, []);

  /**
   * Validate and import JSON
   */
  const handleValidate = useCallback(() => {
    if (!jsonText.trim()) {
      setValidationResult({
        success: false,
        errors: [{ field: 'json', message: 'Veuillez coller le JSON retourne par l\'IA' }]
      });
      return;
    }

    setIsValidating(true);

    // Validate JSON
    const result = validateAIImport(jsonText);
    setValidationResult(result);
    setIsValidating(false);

    // If valid
    if (result.success && result.data) {
      if (showComparison && currentExercice) {
        // Show comparison view
        setValidatedData(result.data);
        setStep('comparison');
      } else {
        // Import directly
        onImport(result.data);
        handleClose();
      }
    }
  }, [jsonText, onImport, handleClose, showComparison, currentExercice]);

  /**
   * Confirm import from comparison view
   * Auto-save is enabled when confirming from comparison
   */
  const handleConfirmImport = useCallback(() => {
    if (validatedData) {
      onImport(validatedData, { autoSave: true });
      handleClose();
    }
  }, [validatedData, onImport, handleClose]);

  /**
   * Go back to input step
   */
  const handleBackToInput = useCallback(() => {
    setStep('input');
    setValidatedData(null);
  }, []);

  /**
   * Render error list
   */
  const renderErrors = (errors: AIImportFieldError[]) => (
    <Box sx={{ mt: 2 }}>
      <Alert severity="error" sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight="medium">
          {errors.length} erreur{errors.length > 1 ? 's' : ''} de validation
        </Typography>
      </Alert>
      <List dense sx={{ bgcolor: 'error.light', borderRadius: 1, p: 1 }}>
        {errors.map((error, index) => (
          <ListItem key={index} sx={{ py: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ErrorIcon color="error" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" color="error.dark">
                  <strong>{error.field}</strong>: {error.message}
                </Typography>
              }
              secondary={
                error.expected && (
                  <Typography variant="caption" color="text.secondary">
                    Attendu: {error.expected}
                  </Typography>
                )
              }
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  /**
   * Render input step
   */
  const renderInputStep = () => (
    <>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Collez le JSON retourne par l'IA ci-dessous. Le JSON sera valide avant l'import.
        </Typography>

        <TextField
          multiline
          fullWidth
          rows={12}
          value={jsonText}
          onChange={handleJsonChange}
          placeholder={`{
  "titre": "...",
  "description": "...",
  "niveau": "facile|moyen|difficile",
  "questions": [...],
  "competencesVisees": [...],
  "tempsEstimeMinutes": 30
}`}
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }
          }}
          error={validationResult?.success === false}
        />

        {/* Validation Results */}
        {validationResult && !validationResult.success && validationResult.errors && (
          renderErrors(validationResult.errors)
        )}

        {validationResult?.success && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
            JSON valide ! Les corrections seront appliquees au formulaire.
          </Alert>
        )}

        {/* Comparison toggle */}
        {currentExercice && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: showComparison ? 'primary.lighter' : 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: showComparison ? 'primary.main' : 'divider'
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showComparison}
                  onChange={handleToggleChange}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompareArrowsIcon fontSize="small" color={showComparison ? 'primary' : 'disabled'} />
                  <Typography variant="body2" fontWeight={showComparison ? 'medium' : 'normal'}>
                    Voir la comparaison avant d'importer
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Help text */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" component="div">
            <strong>Conseils:</strong>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Copiez uniquement le bloc JSON de la reponse de l'IA</li>
              <li>Si le JSON est dans un bloc de code, il sera automatiquement extrait</li>
              <li>Les champs non presents dans le JSON ne seront pas modifies</li>
              <li>Les champs de contexte (matiereName, etc.) seront ignores</li>
            </ul>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Annuler
        </Button>
        <Button
          onClick={handleValidate}
          variant="contained"
          color="primary"
          disabled={!jsonText.trim() || isValidating}
        >
          {isValidating
            ? 'Validation...'
            : showComparison
              ? 'Valider'
              : 'Valider et importer'
          }
        </Button>
      </DialogActions>
    </>
  );

  /**
   * Render comparison step
   */
  const renderComparisonStep = () => (
    <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
      {currentExercice && validatedData && (
        <ComparisonView
          before={currentExercice}
          after={validatedData}
          onConfirm={handleConfirmImport}
          onBack={handleBackToInput}
        />
      )}
    </DialogContent>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '60vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          {step === 'input' ? 'Importer correction IA' : 'Comparaison des modifications'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {step === 'input' ? renderInputStep() : renderComparisonStep()}
    </Dialog>
  );
};

export default AIImportDialog;
